import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../Data/UserContext";
import Modal from "../Modal";
import { CloseIcon } from "../../icons/CloseIcon/CloseIcon";
import { fetchSquaresPrioritisedMatches } from "../Squares/shared/squaresApi";
import {
  mapFilterSquaresPlayableLobbyFixturesForHome,
  isSquaresFixtureLiveForChallengeList,
} from "../Squares/lobby/squaresLobbyMatchUtils";
import { isSquaresGameOpenFromStream } from "../Squares/lobby/squaresLobbyGameOpen";
import { isSquaresLobbyEnabled } from "../Squares/shared/squaresConstants";
import SquaresMatchCard from "../Squares/lobby/squares-match-card";
import { getAuthHeaders } from "../../utils/authHeaders";
import { formatMatchNameAbbreviated } from "../../utils/matchNameDisplay";
import {
  trackGameChallengeFlowOpened,
  trackGameChallengeSent,
} from "../../utils/frontendTrackingEvents";
import { whenModalHistoryIdle } from "../../utils/modalHistoryBack";
import { friendFacingName } from "../../utils/friendDisplayName";

export type SquaresChallengeFriend = {
  userId: number;
  player_name?: string | null;
  display_name?: string | null;
  username?: string | null;
};

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  friend: SquaresChallengeFriend | null;
  onChallengeSent?: () => void;
  /** GTM `entry_point` on challenge flow / sent (e.g. my_zone_friends, public_profile). */
  analyticsEntryPoint?: string;
  /** GTM `game_type` (default `squares`). Set when this flow is reused for other games. */
  analyticsGameType?: string;
  /**
   * Leave this modal and open the challenged match.
   * Profile overlay supplies this so the profile closes before navigation.
   * When omitted, this modal closes itself and navigates after its history entry is released.
   */
  onOpenMatch?: (href: string) => void;
};

type LobbyFixture = Record<string, unknown> & { match_id?: string | number };

type ChallengeSuccessEntry = {
  key: string;
  matchId: string;
  matchLabel: string;
  friendLabel: string;
  /** false when match left lobby or finished; omit/undefined = legacy API, keep link */
  matchOpen?: boolean;
};

function matchLabelFromChallengePayload(matchName: string | null | undefined): string {
  const a = formatMatchNameAbbreviated(matchName);
  if (a) return a;
  return "a match";
}

/** One row per match (API / UI can repeat the same match_id for multiple notifications or sends). */
function dedupeChallengeEntriesByMatchId(entries: ChallengeSuccessEntry[]): ChallengeSuccessEntry[] {
  const seen = new Set<string>();
  const out: ChallengeSuccessEntry[] = [];
  for (const e of entries) {
    const m = String(e.matchId ?? "").trim();
    if (!m) continue;
    if (seen.has(m)) continue;
    seen.add(m);
    out.push(e);
  }
  return out;
}

function subscribeToSquaresLobbyStream(
  matchIds: string[],
  streamDataPerMatchId: React.MutableRefObject<Map<string, unknown>>,
  bump: () => void
): (() => void) | void {
  if (matchIds.length === 0) return;

  const baseApi = process.env.STREAM_API_URL;
  if (!baseApi) return;

  const url = `${baseApi}?matchIds=${matchIds.join(",")}&tier=lobby`;
  const es = new EventSource(url, { withCredentials: false });

  const onMessage = (event: MessageEvent) => {
    try {
      const parsedData = JSON.parse(event.data);
      if (!parsedData?.feed_id) return;
      const matchId = parsedData.feed_id;
      const existing = streamDataPerMatchId.current.get(matchId) || {};
      streamDataPerMatchId.current.set(matchId, { ...existing, ...parsedData });
      bump();
    } catch {
      /* ignore */
    }
  };

  es.addEventListener("message", onMessage);
  es.addEventListener("error", () => {});

  return () => {
    try {
      es.removeEventListener("message", onMessage);
      es.close();
    } catch {
      /* ignore */
    }
  };
}

export default function SquaresChallengeFriendModal({
  isOpen,
  onRequestClose,
  friend,
  onChallengeSent,
  analyticsEntryPoint,
  analyticsGameType,
  onOpenMatch,
}: Props) {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const challengeFlowOpenedTrackedRef = useRef(false);
  const challengeGameType = useMemo(() => {
    const s = analyticsGameType?.trim().toLowerCase();
    return s && s.length > 0 ? s : "squares";
  }, [analyticsGameType]);
  const [allFixtures, setAllFixtures] = useState<LobbyFixture[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [successEntries, setSuccessEntries] = useState<ChallengeSuccessEntry[]>([]);
  const [priorChallengesLoading, setPriorChallengesLoading] = useState(false);
  const streamDataPerMatchId = useRef(new Map<string, unknown>());
  const [streamTick, setStreamTick] = useState(0);
  const bumpStream = useCallback(() => setStreamTick((t) => t + 1), []);

  const matchIdsKey = useMemo(
    () =>
      allFixtures
        .map((f) => String(f.match_id ?? "").trim())
        .filter(Boolean)
        .sort()
        .join(","),
    [allFixtures]
  );

  /** Finished challenges are omitted; at most one row per match_id. */
  const visibleSuccessEntries = useMemo(
    () =>
      dedupeChallengeEntriesByMatchId(successEntries.filter((e) => e.matchOpen !== false)),
    [successEntries]
  );

  const loadFixtures = useCallback(() => {
    if (!isSquaresLobbyEnabled()) {
      setAllFixtures([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    streamDataPerMatchId.current.clear();
    fetchSquaresPrioritisedMatches()
      .then((data: { events?: unknown[] }) => {
        const events = Array.isArray(data?.events) ? data.events : [];
        setAllFixtures(mapFilterSquaresPlayableLobbyFixturesForHome(events) as LobbyFixture[]);
      })
      .catch(() => {
        setAllFixtures([]);
        setError("Could not load matches.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!isOpen || !friend) return;
    loadFixtures();
  }, [isOpen, friend, loadFixtures]);

  useEffect(() => {
    if (!isOpen) {
      challengeFlowOpenedTrackedRef.current = false;
      setSuccessEntries([]);
      setError(null);
      setPriorChallengesLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !friend || user?.userId == null) return;
    if (!analyticsEntryPoint?.trim()) return;
    if (challengeFlowOpenedTrackedRef.current) return;
    challengeFlowOpenedTrackedRef.current = true;
    trackGameChallengeFlowOpened({
      user_id: user.userId,
      game_type: challengeGameType,
      entry_point: analyticsEntryPoint.trim(),
      pathname: location.pathname,
    });
  }, [isOpen, friend, user?.userId, analyticsEntryPoint, challengeGameType, location.pathname]);

  useEffect(() => {
    if (!isOpen || !friend) return;
    const fid = friend.userId;
    const fl =
      friendFacingName(friend) || "your friend";
    let cancelled = false;
    setPriorChallengesLoading(true);
    void (async () => {
      try {
        const res = await fetch(
          `${process.env.API_URL}/account/friends/squares-challenges-sent?friend_id=${encodeURIComponent(String(fid))}`,
          { headers: getAuthHeaders() }
        );
        const data = (await res.json().catch(() => ({}))) as {
          challenges?: Array<{
            id: number;
            match_id?: string | null;
            match_name?: string | null;
            match_open?: boolean;
          }>;
        };
        if (cancelled) return;
        if (!res.ok) {
          setSuccessEntries([]);
          return;
        }
        const list = Array.isArray(data.challenges) ? data.challenges : [];
        const entries = dedupeChallengeEntriesByMatchId(
          list
            .filter((c) => c.match_id != null && String(c.match_id).trim())
            .map((c) => ({
              key: `prior-${c.id}`,
              matchId: String(c.match_id),
              matchLabel: matchLabelFromChallengePayload(c.match_name),
              friendLabel: fl,
              matchOpen: c.match_open,
            }))
        ).slice(0, 8);
        setSuccessEntries(entries);
      } catch {
        if (!cancelled) setSuccessEntries([]);
      } finally {
        if (!cancelled) setPriorChallengesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, friend?.userId, friend?.player_name, friend?.username]);

  useEffect(() => {
    if (!isOpen || !friend) return;
    const onChallengesDirty = () => {
      loadFixtures();
      const fid = friend.userId;
      const fl = friendFacingName(friend) || "your friend";
      setPriorChallengesLoading(true);
      void (async () => {
        try {
          const res = await fetch(
            `${process.env.API_URL}/account/friends/squares-challenges-sent?friend_id=${encodeURIComponent(String(fid))}`,
            { headers: getAuthHeaders() }
          );
          const data = (await res.json().catch(() => ({}))) as {
            challenges?: Array<{
              id: number;
              match_id?: string | null;
              match_name?: string | null;
              match_open?: boolean;
            }>;
          };
          if (!res.ok) return;
          const list = Array.isArray(data.challenges) ? data.challenges : [];
          const entries = dedupeChallengeEntriesByMatchId(
            list
              .filter((c) => c.match_id != null && String(c.match_id).trim())
              .map((c) => ({
                key: `prior-${c.id}`,
                matchId: String(c.match_id),
                matchLabel: matchLabelFromChallengePayload(c.match_name),
                friendLabel: fl,
                matchOpen: c.match_open,
              }))
          ).slice(0, 8);
          setSuccessEntries(entries);
        } finally {
          setPriorChallengesLoading(false);
        }
      })();
    };
    window.addEventListener("c8-squares-challenges-dirty", onChallengesDirty);
    return () => window.removeEventListener("c8-squares-challenges-dirty", onChallengesDirty);
  }, [friend, isOpen, loadFixtures]);

  useEffect(() => {
    if (!isOpen || matchIdsKey.length === 0) return;
    const ids = matchIdsKey.split(",").filter(Boolean);
    const cleanup = subscribeToSquaresLobbyStream(ids, streamDataPerMatchId, bumpStream);
    return () => {
      cleanup?.();
    };
  }, [isOpen, matchIdsKey, bumpStream]);

  const liveFixtures = useMemo(() => {
    return allFixtures.filter((f) => {
      const mid = String(f.match_id ?? "").trim();
      if (!mid) return false;
      const streamData = streamDataPerMatchId.current.get(mid);
      if (!isSquaresFixtureLiveForChallengeList(f, streamData)) return false;
      return isSquaresGameOpenFromStream(f, streamData);
    });
  }, [allFixtures, streamTick]);

  const openChallengedMatch = useCallback(
    (matchId: string) => {
      const href = `/squares?match=${encodeURIComponent(matchId)}`;
      if (onOpenMatch) {
        onOpenMatch(href);
        return;
      }
      onRequestClose();
      whenModalHistoryIdle(() => navigate(href));
    },
    [navigate, onOpenMatch, onRequestClose]
  );

  const sendChallenge = useCallback(
    async (matchId: string) => {
      if (!friend) return;
      setSendingId(matchId);
      setError(null);
      try {
        const res = await fetch(`${process.env.API_URL}/account/friends/squares-challenge`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({ friend_id: friend.userId, match_id: matchId }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const code = data?.error;
          if (code === "not_friends") setError("You can only challenge friends.");
          else if (code === "match_not_eligible") setError("That match is no longer in the Squares lobby.");
          else setError("Could not send challenge. Try again.");
          return;
        }
        if (user?.userId != null && analyticsEntryPoint?.trim()) {
          trackGameChallengeSent({
            user_id: user.userId,
            game_type: challengeGameType,
            entry_point: analyticsEntryPoint.trim(),
            challenged_friend_user_id: friend.userId,
            challenge_target_id: matchId,
            pathname: location.pathname,
          });
        }
        onChallengeSent?.();
        window.dispatchEvent(new CustomEvent("c8-squares-challenges-dirty"));
        openChallengedMatch(matchId);
      } catch {
        setError("Could not send challenge. Try again.");
      } finally {
        setSendingId(null);
      }
    },
    [friend, onChallengeSent, user?.userId, analyticsEntryPoint, challengeGameType, location.pathname, openChallengedMatch]
  );

  const friendLabel =
    friendFacingName(friend) || "your friend";

  const lobbyOff = !isSquaresLobbyEnabled();

  /** Above ProfileModalShell (z-10050) so challenge opens in front of the profile overlay. */
  const CHALLENGE_MODAL_OVERLAY_Z = 10060;
  const CHALLENGE_MODAL_CONTENT_Z = 10061;

  return (
    <Modal
      isOpen={isOpen && friend != null}
      onRequestClose={onRequestClose}
      contentLabel="Challenge friend to Squares"
      ariaHideApp={false}
      style={{
        content: {
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          marginRight: "-50%",
          transform: "translate(-50%, -50%)",
          padding: 0,
          background: "transparent",
          width: "min(560px, calc(100vw - 16px))",
          maxWidth: "100%",
          maxHeight: "min(90vh, 720px)",
          overflow: "hidden",
          zIndex: CHALLENGE_MODAL_CONTENT_Z,
        },
        overlay: {
          background: "rgba(11, 2, 18, 0.8)",
          zIndex: CHALLENGE_MODAL_OVERLAY_Z,
        },
      }}
    >
      <div className="flex max-h-[min(90vh,720px)] flex-col overflow-hidden border border-[#2d1548] bg-[#2a0a55] shadow-[0_12px_40px_rgba(0,0,0,0.45)]">
        <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 bg-[#e470ff] py-2 pl-3 pr-1">
          <span className="min-w-0" aria-hidden />
          <h2 className="text-center font-anton text-[17px] font-bold uppercase leading-none tracking-wide text-white max-[420px]:text-[15px]">
            Squares challenge
          </h2>
          <div className="flex min-w-0 justify-end">
            <button
              type="button"
              onClick={onRequestClose}
              className="!flex !h-11 !w-11 shrink-0 !items-center !justify-center text-white transition-opacity hover:opacity-80"
              aria-label="Close"
            >
              <CloseIcon className="text-white" size={28} />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-[#461d79] px-3 py-3 sm:px-4 sm:py-4">
          {priorChallengesLoading ? (
            <div className="rounded border border-[#d4ff1f]/35 bg-[#1b062f]/55 px-3 py-2.5 text-center font-chakra text-[14px] leading-snug text-white/70 max-[420px]:text-[13px]">
              Loading…
            </div>
          ) : visibleSuccessEntries.length === 0 ? (
            <div className="rounded border border-[#d4ff1f]/35 bg-[#1b062f]/55 px-3 py-2.5 text-center font-chakra text-[14px] leading-snug text-white max-[420px]:text-[13px]">
              <p>
                Challenge <span className="font-semibold">{friendLabel}</span> to play
                <span className="font-semibold text-[#d4ff1f]"> Squares</span> with you.
              </p>
            </div>
          ) : (
            <ul className="space-y-3" aria-live="polite">
              {visibleSuccessEntries.map((entry) => (
                <li key={entry.matchId} className="space-y-2">
                  <div className="border border-[#d4ff1f]/35 bg-[#1b062f]/55 px-3 py-2.5 text-center">
                    <p className="font-chakra text-[13px] leading-snug text-white sm:text-[14px]">
                      You challenged <span className="font-semibold">{entry.friendLabel}</span> to match{" "}
                      <span className="font-semibold text-[#d4ff1f]">{entry.matchLabel}</span>.
                    </p>
                    <Link
                      to={`/squares?match=${encodeURIComponent(entry.matchId)}`}
                      onClick={(e) => {
                        e.preventDefault();
                        openChallengedMatch(entry.matchId);
                      }}
                      className="inline-block font-chakra text-[13px] font-semibold text-[#d4ff1f] underline decoration-[#d4ff1f]/55 underline-offset-2 hover:text-white hover:decoration-white sm:text-[14px]"
                    >
                      Open match in Squares
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {error ? (
            <p
              className="font-chakra text-[13px] font-semibold sm:text-[14px]"
              style={{ color: "#ef4444" }}
              role="alert"
              aria-live="polite"
            >
              {error}
            </p>
          ) : null}
          {lobbyOff ? (
            <p className="font-chakra text-[14px] leading-snug text-white/70">
              The Squares lobby is currently unavailable.
            </p>
          ) : loading ? (
            <p className="text-center font-chakra text-sm text-white/60">Loading…</p>
          ) : liveFixtures.length === 0 ? (
            <p className="font-chakra text-[14px] leading-snug text-white/70">
              No <strong className="text-white">live</strong> Squares matches right now. When a game is live, it will
              show up here.
            </p>
          ) : (
            <div
              className="flex flex-col gap-3 pb-1"
              style={{
                background: "linear-gradient(88.89deg, #652DAF 0%, #623998 100%)",
              }}
            >
              {liveFixtures.map((fixture) => {
                const mid = String(fixture.match_id ?? "").trim();
                const streamData = streamDataPerMatchId.current.get(mid);
                return (
                  <SquaresMatchCard
                    key={mid}
                    fixture={fixture}
                    streamData={streamData}
                    challengePickMode
                    challengeCompactTeamRow
                    onChallengePick={() => sendChallenge(mid)}
                    challengeSending={sendingId === mid}
                    challengeOtherSending={sendingId != null && sendingId !== mid}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
